import React from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  MonthlyPoint,
  formatMonthLabel,
  formatMonthTick,
} from "../../helpers/dashboardMetrics";

interface TrendCardProps {
  title: string;
  series: MonthlyPoint[];
  color: string;
  formatValue: (value: number) => string;
  // Axis ticks get an abbreviated form; a full kroner amount does not fit in
  // the gutter. Defaults to formatValue when the two can be the same.
  formatTick?: (value: number) => string;
  // Printed under the plot. Used to say which months the chart covers, so a
  // missing final month reads as deliberate rather than as missing data.
  note?: string;
}

// One measure per card. Two measures of different scale get two cards rather
// than a second Y axis, which would let the shape of either line be tuned by
// choosing its scale.
export const TrendCard: React.FC<TrendCardProps> = ({
  title,
  series,
  color,
  formatValue,
  formatTick = formatValue,
  note,
}) => (
  <div className="bg-white rounded-2xl shadow p-5">
    <h3 className="text-sm font-medium text-gray-700 mb-4">{title}</h3>

    {series.length === 0 ? (
      <div className="h-[240px] flex items-center justify-center text-sm text-gray-400">
        No data for this period
      </div>
    ) : (
      <div className="h-[240px]">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={series} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
            <CartesianGrid stroke="#f0f0f0" vertical={false} />
            <XAxis
              dataKey="month"
              tickFormatter={formatMonthTick}
              tickLine={false}
              axisLine={false}
              tick={{ fontSize: 12, fill: "#888888" }}
            />
            <YAxis
              tickFormatter={formatTick}
              tickLine={false}
              axisLine={false}
              width={70}
              tick={{ fontSize: 12, fill: "#888888" }}
            />
            <Tooltip
              labelFormatter={formatMonthLabel}
              formatter={(value: number) => [formatValue(value), title]}
            />
            <Line
              type="monotone"
              dataKey="value"
              stroke={color}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    )}

    {note && <p className="text-xs text-gray-400 mt-3">{note}</p>}
  </div>
);
